import { useQuery } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GET_KURSUS_LINJE_AUTOCOMPLETE_QUERY } from "../../api/kursus/queries";
// import FirmaIcon from "../icons/FirmaIcon";
import AutoComplete, { AutoCompleteOnSelectedItemChange } from "./AutoComplete";
import {
  AutoCompleteItem,
  KursusAutoCompleteResponse,
  LinjeAutoCompleteResponse,
  isKursusAutoCompleteResponse,
} from "./interface";

export type AutoCompleteKursusLinjeResponse = {
  fmulinjerByNr: {
    edges: {
      node: LinjeAutoCompleteResponse;
    }[];
  };
  kursusholdsByNr: {
    edges: {
      node: KursusAutoCompleteResponse;
    }[];
  };
  fmulinjer: {
    edges: {
      node: LinjeAutoCompleteResponse;
    }[];
  };
  kursusholds: {
    edges: {
      node: KursusAutoCompleteResponse;
    }[];
  };
};

export type AutoCompleteKursusLinjeOnSelectedItem =
  | AutoCompleteItem<AutoCompleteKursusLinjeResponse | undefined>
  | null
  | undefined;

export default function AutoCompleteKursusLinje({
  className,
  clearAfterSelection,
  includeFirstItem,
  onSelectedItemChange,
  placeholder,
  sizeLarge,
  sizeSmall,
}: {
  className?: string;
  sizeLarge?: boolean;
  sizeSmall?: boolean;
  clearAfterSelection?: boolean;
  includeFirstItem?: boolean;
  onSelectedItemChange?: AutoCompleteOnSelectedItemChange<
    AutoCompleteKursusLinjeResponse | undefined
  >;
  placeholder?: string;
}) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const { data, loading } = useQuery<AutoCompleteKursusLinjeResponse>(
    GET_KURSUS_LINJE_AUTOCOMPLETE_QUERY({
      q: inputValue,
    }),
    { variables: { q: inputValue }, skip: !inputValue },
  );
  const kursusLinjer = data
    ? [
        ...data.fmulinjerByNr.edges.map((e) => e.node),
        ...data.kursusholdsByNr.edges.map((e) => e.node),
        ...data.fmulinjer.edges.map((e) => e.node),
        ...data.kursusholds.edges.map((e) => e.node),
      ]
    : [];
  const ids = kursusLinjer.map((p) => p.id);
  const uniqueKursusLinjer = kursusLinjer.filter(
    (p, index) => ids.indexOf(p.id) === index,
  );

  const items =
    !loading && kursusLinjer.length > 0
      ? uniqueKursusLinjer.map((p) => {
          const item: AutoCompleteItem<AutoCompleteKursusLinjeResponse> = {
            id: p.id,
            header: `${p.navn}`,
            subheader: isKursusAutoCompleteResponse(p)
              ? `${p.holdNr} | ${
                  p.fmukursusKategoriNavigation
                    ? p.fmukursusKategoriNavigation.navn
                    : "Ingen kategori"
                }`
              : `${p.nr}`,
            icon: <div>{p.__typename === "KursusholdDTO" ? "K" : "L"}</div>,
            href:
              p.__typename === "KursusholdDTO"
                ? `kursus/${p.id}`
                : `linje/${p.id}`,
            // icon: <FirmaIcon width={20} height={20} />,
          };
          return item;
        })
      : [];

  const defaultOnSelectedItemChange: AutoCompleteOnSelectedItemChange<
    AutoCompleteKursusLinjeResponse
  > = ({ selectedItem }) => {
    if (selectedItem) {
      if (selectedItem.href) {
        navigate(`/${selectedItem.href}`, { replace: false });
      } else {
        const path = window.location.pathname;
        navigate(
          path.toLowerCase().includes("kursus")
            ? `/kursus?q=${inputValue}`
            : `/linje?q=${inputValue}`,
          {
            replace: true,
          },
        );
      }
    }
  };

  return (
    <AutoComplete<AutoCompleteKursusLinjeResponse>
      className={className}
      sizeLarge={sizeLarge}
      sizeSmall={sizeSmall}
      clearAfterSelection={clearAfterSelection}
      includeFirstItem={includeFirstItem}
      items={items}
      itemToString={(item) => (item?.header as string) ?? ""}
      loading={loading}
      placeholder={placeholder ?? "Søg på kurser og linjer ..."}
      onSelectedItemChange={onSelectedItemChange ?? defaultOnSelectedItemChange}
      setInputValue={setInputValue}
    />
  );
}
